import React, { useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Layout } from 'antd';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

import LfContent from './LfContent';
import ByContent from './ByContent';
import DetailPage from './DetailPage';
import useIsMobile from './useIsMobile';

const { Header, Sider, Content } = Layout;

// 页签只提供文案，跳转统一交给 Menu 的 onClick：
// 手指点中菜单项文字以外的区域时，事件会落在外层 li 上（不会进到 a），
// 只有由 onClick 驱动跳转才能保证任何位置点击都能切换页面
const items = [
  { key: 'lfContent', label: 'LF内容' },
  { key: 'byContent', label: 'BY内容' },
];

// 页签高亮只由路由推导，避免出现"高亮切到 BY、页面还停在 LF"的状态脱钩
const getSelectedKey = (pathname) => {
  const [first, second] = pathname.substring(1).split('/');
  if (first === 'byContent') {
    return 'byContent';
  }
  if (first === 'detail' && second === 'by') {
    return 'byContent';
  }
  return 'lfContent';
};

const MainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const selectedKey = getSelectedKey(location.pathname);

  useEffect(() => {
    // 直接打开根路径时回到默认页签
    if (window.location.pathname === '/') {
      navigate('/lfContent');
    }
  }, [navigate, location]);

  const onClick = ({ key }) => {
    // 已经在目标页签上时不重复入栈
    if (location.pathname !== `/${key}`) {
      navigate(`/${key}`);
    }
  };

  // 安卓物理返回键：详情页先退出详情，列表页再按才退出 App
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return undefined;
    }

    const subscription = CapacitorApp.addListener('backButton', () => {
      const path = window.location.pathname;
      if (path.startsWith('/detail/')) {
        navigate(-1);
      } else if (path !== '/lfContent' && path !== '/byContent') {
        navigate('/lfContent');
      } else {
        CapacitorApp.exitApp();
      }
    });

    return () => {
      subscription.then((handle) => handle.remove()).catch(() => {});
    };
  }, [navigate]);

  // 从信息流进入详情时，把列表页当作"背景路由"继续挂载在页面上：
  // 返回后列表数据、已选城市、页码、滚动位置都不会丢失
  const backgroundLocation = location.state && location.state.backgroundLocation;

  const routes = (
    <Routes location={backgroundLocation || location}>
      <Route path="/lfContent" element={<LfContent />} />
      <Route path="/byContent" element={<ByContent />} />
      <Route path="/detail/:type/:id" element={<DetailPage />} />
    </Routes>
  );

  // 详情页浮层（仅从信息流点卡片进入时出现，直接打开链接则是普通整页）
  const detailOverlay = backgroundLocation ? (
    <Routes>
      <Route path="/detail/:type/:id" element={<DetailPage overlay />} />
    </Routes>
  ) : null;

  if (isMobile) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            height: 'auto',
            minHeight: 46,
            lineHeight: 'normal',
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div className="demo-logo" />
          <Menu
            theme="light"
            onClick={onClick}
            // lineHeight 撑满表头：不设时页签会继承表头的 normal，只有约 20px 高，手指很容易点空
            style={{ flex: 1, minWidth: 0, lineHeight: '46px', borderBottom: 'none' }}
            selectedKeys={[selectedKey]}
            mode="horizontal"
            items={items}
          />
        </Header>

        <Content style={{ padding: 12 }}>{routes}</Content>
        {detailOverlay}
      </Layout>
    );
  }

  return (
    <Layout>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >   
        <div className="demo-logo" />
      </Header>
      
      <Layout>
        <Sider width={200} theme="light">
          <Menu
            theme="light"
            onClick={onClick}
            style={{ width: 200 }}
            selectedKeys={[selectedKey]} 
            mode="inline"
            items={items}
          />
        </Sider>
        
        <Layout style={{ padding: '0 24px 24px' }}>
          <div style={{ flex: 1, padding: '20px', overflowX: 'auto' }}>
            {routes}
          </div>
        </Layout>
        {detailOverlay}
      </Layout>
    </Layout>
  );
};

export default MainPage;
